/**
 * FILE: conversation-page-settings.service.ts
 * CHỨC NĂNG: Cache cài đặt của các page đang đăng nhập cho trang hội thoại (desktop + mobile).
 * TÁC DỤNG: Thay `ConversationService.dataSettingPages` của FE13. Tải `/chat/settingpages` kèm
 * `checkBotId` một lần khi vào trang hội thoại; BE gắn thêm `hasBotId` (page có chatbot) và
 * `hasChaseScriptIds` (page thuộc kịch bản bám đuổi) để hiện nút Tạm ngừng chatbot. Thanh nhãn
 * (gợi ý phím tắt) và dialog tạo đơn (tự gắn nhãn) cũng đọc cài đặt page từ đây.
 */
import {Injectable, inject, signal} from '@angular/core';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {CHANNEL} from '@features/conversation/models/channel.model';
import {IConversation} from '@features/conversation/models/conversation.model';

/** `settings` của page (BE lưu nguyên JSON) + 2 trường BE gắn thêm khi gọi kèm `checkBotId`. */
export interface IConversationPageSetting {
  /** Id chatbot đang chạy trên page. */
  hasBotId?: string;
  /** Id các kịch bản bám đuổi có chọn page. */
  hasChaseScriptIds?: string[];
  [key: string]: unknown;
}

/** Kênh có chatbot / kịch bản bám đuổi chạy theo từng hội thoại (FE13: nút ph-robot ở header). */
const AUTO_REPLY_CHANNELS: number[] = [CHANNEL.FACEBOOK, CHANNEL.ZALO, CHANNEL.SHOPEE, CHANNEL.ZALO_PERSONAL];

@Injectable({providedIn: 'root'})
export class ConversationPageSettingsService {
  private readonly api = inject(ConversationApiService);
  private readonly _settings = signal<Record<string, IConversationPageSetting>>({});

  load(pageIds: string[]): void {
    if (!pageIds.length) return;
    this.api.getPageSettings({pageIds, checkBotId: true}).subscribe(res => {
      if (res.code !== 1) return;
      const items = res.data?.items;
      // PHP trả `[]` khi không page nào có cài đặt.
      this._settings.set(items && typeof items === 'object' && !Array.isArray(items) ? items : {});
    });
  }

  get(pageId: string | null | undefined): IConversationPageSetting | null {
    return pageId ? this._settings()[pageId] ?? null : null;
  }

  /**
   * Trang Cài đặt vừa lưu một phần cài đặt của page (vd: hiển thị gợi ý nhãn): cập nhật tại chỗ,
   * không tải lại toàn bộ. Lần vào hội thoại sau load() vẫn lấy lại đầy đủ từ BE.
   */
  patch(pageId: string, partial: Partial<IConversationPageSetting>): void {
    if (!pageId) return;
    this._settings.update(settings => ({...settings, [pageId]: {...settings[pageId], ...partial}}));
  }

  /** Page của hội thoại có chatbot hoặc kịch bản bám đuổi → cho tạm ngừng trả lời tự động. */
  canPauseAutoReply(conversation: IConversation | null | undefined): boolean {
    if (!conversation?.fanpageId || !AUTO_REPLY_CHANNELS.includes(conversation.channel)) return false;
    const setting = this.get(conversation.fanpageId);
    return !!(setting?.hasBotId || setting?.hasChaseScriptIds?.length);
  }
}
